/**
 * The invoice number format, as the UI understands it.
 *
 * Two implementations of one format exist — `invoice-number-format.js` on the
 * server and this — because the preview has to update as the user types, before
 * anything is saved. That is a drift risk, so the rendering table here is
 * deliberately the same one the backend test uses. The backend stays the
 * authority and revalidates every save.
 *
 * Shared by the Orders-settings card and the invoice series modal; it lived in
 * the card until the second caller appeared.
 */

export const NO_DATE = "{SERIES}{N}";
export const YEARLY = "{SERIES}-{YYYY}-{N:4}";
export const MONTHLY = "{SERIES}-{YYYY}{MM}-{N:3}";
export const DAILY = "{SERIES}-{YY}{MM}{DD}-{N}";

export const PRESETS = [NO_DATE, YEARLY, MONTHLY, DAILY];
export const CUSTOM = "custom";

/**
 * Render a template the way the backend will, so the preview is honest before
 * anything is saved. Kept deliberately small: the backend is the authority and
 * revalidates, this only has to show the shape.
 */
export function renderPreview(
  template: string,
  seriesTitle: string,
  sequence: number,
  at: Date = new Date()
): string {
  const label =
    (seriesTitle || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 20) || "SF";
  const pad = (n: number, width: number) => String(n).padStart(width, "0");
  const seq = Math.max(1, Math.floor(sequence) || 1);

  return template.replace(
    /\{(SERIES|YYYY|YY|MM|DD|N)(?::(\d+))?\}/g,
    (_match, token, width) => {
      switch (token) {
        case "SERIES":
          return label;
        case "YYYY":
          return String(at.getFullYear());
        case "YY":
          return pad(at.getFullYear() % 100, 2);
        case "MM":
          return pad(at.getMonth() + 1, 2);
        case "DD":
          return pad(at.getDate(), 2);
        default:
          return pad(seq, Number(width) || 1);
      }
    }
  );
}

/**
 * The cheap half of the backend's template rules, so a hand-typed format fails
 * under the field rather than on save.
 *
 * Deliberately a SUBSET: `parseNumberTemplate` on the server stays the
 * authority and re-checks everything this skips — token ordering (a {DD} with
 * no {MM}), allowed literals, and the 64-character length budget. This only
 * catches the mistakes that are obvious while typing.
 */
export function validateTemplate(
  template: string
): { key: string; opts: Record<string, any> } | null {
  const counters = (template.match(/\{N(?::\d+)?\}/g) || []).length;
  if (counters !== 1) {
    return {
      key: "invoice_number_error_counter",
      opts: { defaultValue: "The format needs exactly one {N}." },
    };
  }
  if (!template.includes("{SERIES}")) {
    return {
      key: "invoice_number_error_series",
      opts: { defaultValue: "The format must contain {SERIES}." },
    };
  }
  const unknown = template.match(
    /\{(?!(?:SERIES|YYYY|YY|MM|DD|N(?::\d+)?)\})[^}]*\}/g
  );
  if (unknown && unknown.length) {
    return {
      key: "invoice_number_error_token",
      opts: {
        token: unknown[0],
        defaultValue: "{{token}} is not one of the tokens listed below.",
      },
    };
  }
  return null;
}

